import {
  Button,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { useTheme } from 'styled-components';

import { FilterProps } from '../..';
import { Container, IconButton, ItemSelect } from './styles';

export function SelectFilter(props: FilterProps): JSX.Element {
  const { style, column, values, title } = props;
  const value = column?.column?.getFilterValue() as string[];

  const [selected, setSelected] = useState<string[]>(value || ([] as string[]));

  const {
    colors: { primary, lighterText },
  } = useTheme();
  const { t } = useTranslation();

  const handleSelectItem = useCallback(
    (item: string) => {
      if (selected.includes(item)) {
        setSelected((selectedItems) =>
          selectedItems.filter((selectedItem) => item !== selectedItem),
        );
        return;
      }

      setSelected((selectedItems) => [...selectedItems, item]);
    },
    [selected],
  );

  const handleFilter = useCallback(() => {
    if (isEmpty(selected)) {
      column?.column?.setFilterValue(undefined);
      return;
    }

    column?.column?.setFilterValue(selected);
  }, [column, selected]);

  const handleResetFilter = useCallback(() => {
    column?.column?.setFilterValue(undefined);
    setSelected([]);
  }, [column]);

  const renderContent = useMemo(() => {
    return values.map((item) => (
      <ItemSelect key={item} onClick={() => handleSelectItem(item)}>
        <div className="check-container">
          {selected.includes(item) && <FaCheck />}
        </div>
        <p>{item}</p>
      </ItemSelect>
    ));
  }, [selected, handleSelectItem, values]);

  const renderFooter = useMemo(() => {
    return (
      <PopoverFooter>
        <Button
          size="md"
          bg={primary}
          color={lighterText}
          onClick={handleFilter}
          disabled={isEmpty(selected)}
          _hover={{
            bg: primary,
            filter: 'brightness(120%)',
          }}
        >
          {t('filter.filter_action')}
        </Button>
        <Button
          size="md"
          variant="outline"
          ml="4"
          onClick={handleResetFilter}
          disabled={isEmpty(value)}
        >
          {t('buttons.clear_filters')}
        </Button>
      </PopoverFooter>
    );
  }, [
    primary,
    lighterText,
    handleFilter,
    selected,
    t,
    handleResetFilter,
    value,
  ]);

  return (
    <Popover>
      <PopoverTrigger>
        <Container>
          <IconButton>
            <FiFilter size={14} color={style?.color} />
          </IconButton>
        </Container>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <PopoverArrow />
          <PopoverHeader>{title || t('buttons.filters')}</PopoverHeader>
          <PopoverCloseButton />
          <PopoverBody maxHeight={400} overflowY="scroll">
            {renderContent}
          </PopoverBody>
          {renderFooter}
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
